import { useState } from 'react';
import { MethodApiId } from '../../data/methods';
import { useCurrencySubmission } from '../../hooks';
import {
  binancePayAsBlockchain,
  binancePayDefaultCurrency,
} from '../../utils/enums';
import { Currency } from '../CurrencySelection/CurrencySelection';
import { useBinancePay } from './useBinancePay';
import { MethodCategory } from './MethodSelection';
import { PaymentMethod } from '../../graphql/customTypes';

interface UseMethodSelectionProps {
  onSelectionError: () => void;
  onCurrencySelectionRequired: (category: MethodCategory) => void;
  currencies: Currency[];
  eurAmount: string;
  travelRuleEnabled: boolean;
}

const MIN_EUR_AMOUNT_FOR_WALLET_AGREEMENT = 1000;

export const useMethodSelection = ({
  onSelectionError,
  onCurrencySelectionRequired,
  currencies,
  eurAmount,
  travelRuleEnabled,
}: UseMethodSelectionProps) => {
  const [isBinancePaySelected, setIsBinancePaySelected] = useState(false);
  const [isWalletAgreementOpen, setIsWalletAgreementOpen] = useState(false);

  const { loading, submitCurrencySelection } = useCurrencySubmission({
    onSelectionError,
    onSelectionComplete: () => setIsBinancePaySelected(false),
  });
  const { isBinancePayEnabled, getBinancePayHelperMessage } =
    useBinancePay(currencies);

  const handleAgreementAgree = () => {
    setIsWalletAgreementOpen(false);

    onCurrencySelectionRequired('wallet');
  };

  const handleAgreementClose = () => {
    setIsWalletAgreementOpen(false);
  };

  const isWalletAgreementRequired =
    parseFloat(eurAmount) >= MIN_EUR_AMOUNT_FOR_WALLET_AGREEMENT;

  const handleCategorySelection = (category: MethodCategory) => {
    switch (category) {
      case 'binancePay':
        setIsBinancePaySelected(true);
        submitCurrencySelection({
          currency: binancePayDefaultCurrency,
          network: binancePayAsBlockchain,
          methodId: MethodApiId.BinancePay,
          paymentMethod: PaymentMethod.EXCHANGE,
        });
        break;
      case 'exchange':
        onCurrencySelectionRequired('exchange');
        break;
      case 'wallet':
        if (isWalletAgreementRequired && travelRuleEnabled) {
          return setIsWalletAgreementOpen(true);
        }

        onCurrencySelectionRequired('wallet');
        break;
    }
  };

  return {
    loading,
    isBinancePaySelected,
    isBinancePayEnabled,
    isWalletAgreementOpen,
    handleAgreementClose,
    handleAgreementAgree,
    getBinancePayHelperMessage,
    handleCategorySelection,
  };
};
